import React, { type ReactNode, useState } from 'react';

import classNames from 'classnames';

import styles from './FloatButtonGroup.module.css';

export type FloatButtonShape = 'square' | 'circle';
export type FloatButtonSize = 'small' | 'medium' | 'large';
export type FloatButtonType = 'default' | 'primary';
export type FloatButtonPlacement = 'top' | 'right' | 'bottom' | 'left';

export interface FloatButtonItem {
  key: string;
  icon: ReactNode;
  description: string;
  onClick?: () => void;
  type?: FloatButtonType;
  group?: string;
  toggleable?: boolean;
  isDivider?: boolean;
  disabled?: boolean;
}

export interface FloatButtonGroupProps {
  icon: ReactNode;
  description: string;
  items: FloatButtonItem[];
  shape?: FloatButtonShape;
  size?: FloatButtonSize;
  type?: FloatButtonType;
  placement?: FloatButtonPlacement;
  trigger?: 'click' | 'hover';
  autoCloseOnSelect?: boolean;
  style?: React.CSSProperties;
}

export const FloatButtonGroup: React.FC<FloatButtonGroupProps> = ({
  icon,
  description,
  items,
  shape = 'circle',
  size = 'medium',
  type = 'default',
  placement = 'top',
  trigger = 'click',
  autoCloseOnSelect = true,
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    if (trigger === 'click') {
      setIsOpen(!isOpen);
    }
  };

  const handleMouseEnter = () => {
    if (trigger === 'hover') {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === 'hover') {
      setIsOpen(false);
    }
  };

  const handleItemClick = (item: FloatButtonItem) => {
    if (item.disabled) {
      return;
    }

    if (item.onClick) {
      item.onClick();
    }

    if (autoCloseOnSelect && item.group && !item.toggleable) {
      if (trigger === 'click') {
        setIsOpen(false);
      }
    }
  };

  const getButtonClass = (buttonType: FloatButtonType = 'default', disabled: boolean = false) => {
    return classNames(
      styles.floatButton,
      styles[`floatButton--${shape}`],
      styles[`floatButton--${size}`],
      styles[`floatButton--${buttonType}`],
      {
        [styles['floatButton--disabled']]: disabled,
      }
    );
  };

  const getItemsContainerClass = () => {
    return classNames(styles.itemsContainer, styles[`itemsContainer--${placement}`], {
      [styles['itemsContainer--open']]: isOpen,
    });
  };

  return (
    <div
      className={styles.floatButtonGroup}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <div className={getItemsContainerClass()}>
        {items.map((item) =>
          item.isDivider ? (
            <div key={item.key} className={styles.divider} />
          ) : (
            <button
              key={item.key}
              className={getButtonClass(item.type, item.disabled)}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              type="button">
              <div className={styles.buttonContent}>
                <div className={styles.buttonIcon}>{item.icon}</div>
                <div className={styles.buttonDescription}>{item.description}</div>
              </div>
            </button>
          )
        )}
      </div>

      <button className={classNames(getButtonClass(type), styles.mainButton)} onClick={handleToggle} type="button">
        <div className={styles.buttonContent}>
          <div className={styles.buttonIcon}>{icon}</div>
          <div className={styles.buttonDescription}>{description}</div>
        </div>
      </button>
    </div>
  );
};
